import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ChevronDown,
  Users,
  Calendar,
  CheckCircle2,
  Play,
  Pencil,
  ListTodo,
  Video,
  AlertCircle,
  AlertTriangle,
  Clock,
  Send
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import { PersonalTaskEditDialog } from "./PersonalTaskEditDialog";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica",
  script_videomaker: "Script Videomaker",
  script_cliente: "Script Cliente",
  conferma_script: "Conferma Script",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio ADV",
  appuntamento_successivo: "Appuntamento Successivo",
  report_fine_mese: "Report Fine Mese",
  compilare_statistiche_adv: "Statistiche ADV"
};

const PERIOD_LABELS = { today: "Oggi", week: "Settimana", month: "Mese" };

const SEMAFORO_SECTIONS = [
  { key: "scaduto", label: "Scadute", color: "bg-zinc-600", textColor: "text-white", icon: Clock, borderColor: "border-zinc-300", bgLight: "bg-zinc-50" },
  { key: "rosso", label: "Urgenti (Oggi)", color: "bg-red-500", textColor: "text-white", icon: AlertCircle, borderColor: "border-red-300", bgLight: "bg-red-50" },
  { key: "giallo", label: "In Scadenza", color: "bg-amber-500", textColor: "text-white", icon: AlertTriangle, borderColor: "border-amber-300", bgLight: "bg-amber-50" },
  { key: "verde", label: "In Tempo", color: "bg-emerald-500", textColor: "text-white", icon: CheckCircle2, borderColor: "border-emerald-300", bgLight: "bg-emerald-50" },
  { key: "completato", label: "Completate", color: "bg-slate-400", textColor: "text-white", icon: CheckCircle2, borderColor: "border-slate-200", bgLight: "bg-slate-50" },
];

export function DashboardPMView({ user }) {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("today");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedOps, setExpandedOps] = useState({});
  const [editingTask, setEditingTask] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`/dashboard/grouped-tasks?period=${period}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
        const exp = {};
        json.operatives.forEach(op => { exp[op.user_id] = true; });
        setExpandedOps(exp);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dati");
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (userId) => {
    setExpandedOps(prev => ({ ...prev, [userId]: !prev[userId] }));
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await apiFetch(`/tasks/${taskId}/status?status=${newStatus}`, { method: "PATCH" });
      if (res.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const updatePersonalTaskStatus = async (taskId, newStatus) => {
    try {
      const res = await apiFetch(`/personal-tasks/${taskId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success("Stato aggiornato");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const sendDailySummary = async () => {
    try {
      const res = await apiFetch("/daily-summary");
      if (!res.ok) {
        toast.error("Errore nel caricamento riepilogo");
        return;
      }
      const summary = await res.json();

      // Build email body
      const lines = [];
      lines.push(`RIEPILOGO GIORNALIERO - ${summary.date}`);
      lines.push(`Operatore: ${summary.user_name}`);
      lines.push("");

      if (summary.system_completed.length > 0 || summary.personal_completed.length > 0) {
        lines.push("--- TASK COMPLETATE OGGI ---");
        for (const t of summary.system_completed) {
          const LABELS = { call_strategica: "Call Strategica", script_videomaker: "Script Videomaker", script_cliente: "Script Cliente", conferma_script: "Conferma Script", ped_programmazione: "PED + Programmazione", avvio_adv: "Avvio ADV", appuntamento_successivo: "Appuntamento Successivo" };
          lines.push(`[Auto] ${t.client_name} - ${etichettaTask(t, LABELS)}`);
        }
        for (const t of summary.personal_completed) {
          lines.push(`[Pers.] ${t.client_name || "N/A"} - ${t.title}`);
        }
      } else {
        lines.push("Nessuna task completata oggi.");
      }

      lines.push("");
      if (summary.system_pending.length > 0 || summary.personal_pending.length > 0) {
        lines.push(`--- TASK ANCORA IN SOSPESO (${summary.system_pending.length + summary.personal_pending.length}) ---`);
        for (const t of summary.system_pending.slice(0, 15)) {
          const LABELS = { call_strategica: "Call Strategica", script_videomaker: "Script Videomaker", script_cliente: "Script Cliente", conferma_script: "Conferma Script", ped_programmazione: "PED + Programmazione", avvio_adv: "Avvio ADV", appuntamento_successivo: "Appuntamento Successivo" };
          lines.push(`[Auto] ${t.client_name} - ${etichettaTask(t, LABELS)} - scadenza: ${t.due_date ? new Date(t.due_date).toLocaleDateString("it-IT") : "N/A"}`);
        }
        if (summary.system_pending.length > 15) {
          lines.push(`... e altre ${summary.system_pending.length - 15} task`);
        }
        for (const t of summary.personal_pending.slice(0, 5)) {
          lines.push(`[Pers.] ${t.title}`);
        }
      }

      // Get resp. operativo emails
      const recipients = (summary.responsabili_operativi || []).map(r => r.email).filter(Boolean);

      if (recipients.length === 0) {
        toast.error("Nessun Responsabile Operativo configurato");
        return;
      }

      // Open Gmail immediately (before any async)
      const to = recipients.join(",");
      const subject = encodeURIComponent(`Riepilogo Giornaliero ${summary.date} - ${summary.user_name}`);
      const body = encodeURIComponent(lines.join("\n"));
      window.open(`https://mail.google.com/mail/?view=cm&to=${to}&su=${subject}&body=${body}`, "_blank");
      toast.success("Gmail aperta con il riepilogo");
    } catch (error) {
      toast.error("Errore nel generare il riepilogo");
    }
  };

  const totalTasks = data?.operatives?.reduce((s, op) => s + op.stats.total, 0) || 0;
  const totalScaduti = data?.operatives?.reduce((s, op) => s + op.stats.scaduto, 0) || 0;
  const totalRosso = data?.operatives?.reduce((s, op) => s + op.stats.rosso, 0) || 0;
  const totalGiallo = data?.operatives?.reduce((s, op) => s + op.stats.giallo, 0) || 0;

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-10 bg-muted rounded-lg w-64"></div>
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-muted rounded-xl"></div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 lg:gap-5" data-testid="dashboard-pm-view">
      {/* Period Tabs */}
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-xl border border-slate-200 bg-white/70 px-4 py-3 lg:px-5 shadow-sm">
        <div className="flex gap-1 p-1 bg-muted rounded-lg" data-testid="period-tabs">
          {["today", "week", "month"].map(p => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-all ${
                period === p
                  ? "bg-white shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              data-testid={`period-tab-${p}`}
            >
              {PERIOD_LABELS[p]}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2.5 text-sm flex-wrap">
          <Badge variant="outline" className="text-xs">{data?.operatives?.length || 0} operativi</Badge>
          <Badge variant="outline" className="text-xs">{totalTasks} task</Badge>
          {totalScaduti > 0 && <Badge className="badge-scaduto border text-xs">{totalScaduti} scaduti</Badge>}
          {totalRosso > 0 && <Badge className="badge-rosso border text-xs">{totalRosso} urgenti</Badge>}
          {totalGiallo > 0 && <Badge className="badge-giallo border text-xs">{totalGiallo} in scadenza</Badge>}
          <Button
            variant="outline"
            size="sm"
            className="text-xs sm:ml-2"
            onClick={sendDailySummary}
            data-testid="send-daily-summary-btn"
          >
            <Send className="w-3 h-3 mr-1" />
            Invia Riepilogo
          </Button>
        </div>
      </div>

      {/* Operative Cards */}
      {data?.operatives?.length > 0 ? (
        <div className="flex flex-col gap-3">
          {data.operatives.map(op => (
            <OperativeCard
              key={op.user_id}
              operative={op}
              expanded={!!expandedOps[op.user_id]}
              onToggle={() => toggleExpand(op.user_id)}
              onStatusChange={updateTaskStatus}
              onPersonalStatusChange={updatePersonalTaskStatus}
              onEditPersonalTask={(task) => { setEditingTask(task); setShowEditDialog(true); }}
              navigate={navigate}
            />
          ))}
        </div>
      ) : (
        <Card className="border border-slate-200">
          <CardContent className="p-8 text-center">
            <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4 opacity-50" />
            <p className="text-muted-foreground">Nessun operativo con task per questo periodo</p>
          </CardContent>
        </Card>
      )}

      <PersonalTaskEditDialog
        task={editingTask}
        open={showEditDialog}
        onOpenChange={setShowEditDialog}
        onSaved={fetchData}
      />
    </div>
  );
}

function OperativeCard({ operative, expanded, onToggle, onStatusChange, onPersonalStatusChange, onEditPersonalTask, navigate }) {
  const { name, role, num_clients, system_tasks, personal_tasks, stats } = operative;
  const isVideomaker = role === "videomaker";
  const isUnassigned = operative.user_id === "_unassigned";

  // Combine all tasks
  const allTasks = [
    ...system_tasks.map(t => ({ ...t, source: "system" })),
    ...personal_tasks.map(t => ({ ...t, source: "personal" }))
  ];

  // Group by semaforo status
  const groupedTasks = {};
  for (const section of SEMAFORO_SECTIONS) {
    if (section.key === "completato") {
      groupedTasks[section.key] = allTasks.filter(t => t.status === "completato" || t.semaforo === "completato");
    } else {
      groupedTasks[section.key] = allTasks.filter(t => t.status !== "completato" && t.semaforo !== "completato" && t.semaforo === section.key);
    }
  }

  const roleIcon = isUnassigned ? AlertCircle : (isVideomaker ? Video : Users);
  const RoleIcon = roleIcon;
  const roleBg = isUnassigned ? "bg-orange-100" : (isVideomaker ? "bg-purple-100" : "bg-indigo-100");
  const roleIconColor = isUnassigned ? "text-orange-600" : (isVideomaker ? "text-purple-600" : "text-indigo-600");
  const roleBadgeClass = isUnassigned ? "border-orange-300 text-orange-700" : (isVideomaker ? "border-purple-300 text-purple-700" : "border-indigo-300 text-indigo-700");
  const roleLabel = isUnassigned ? "Non assegnato" : (isVideomaker ? "Videomaker" : "SMM");

  return (
    <Card
      className="border border-slate-200 overflow-hidden"
      data-testid={`operative-card-${operative.user_id}`}
    >
      {/* Header */}
      <div
        className="p-4 lg:px-5 flex items-center justify-between gap-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
        data-testid={`operative-toggle-${operative.user_id}`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${roleBg}`}>
            <RoleIcon className={`w-5 h-5 ${roleIconColor}`} />
          </div>
          <div className="space-y-1.5">
            <h3 className="font-semibold text-foreground">{name}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant="outline" className={`text-xs ${roleBadgeClass}`}>
                {roleLabel}
              </Badge>
              <span>{num_clients} clienti</span>
              <span className="text-slate-300">|</span>
              <span>{stats.total} task</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap justify-end">
          {stats.scaduto > 0 && <Badge className="badge-scaduto border text-xs">{stats.scaduto}</Badge>}
          {stats.rosso > 0 && <Badge className="badge-rosso border text-xs">{stats.rosso}</Badge>}
          {stats.giallo > 0 && <Badge className="badge-giallo border text-xs">{stats.giallo}</Badge>}
          {stats.verde > 0 && <Badge className="badge-verde border text-xs">{stats.verde}</Badge>}
          {stats.completati > 0 && <Badge variant="secondary" className="text-xs">{stats.completati}</Badge>}
          <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ml-1 ${expanded ? "rotate-180" : ""}`} />
        </div>
      </div>

      {/* Task Sections grouped by semaforo */}
      {expanded && (
        <div className="border-t border-slate-200 bg-slate-50/60 p-3 sm:p-4">
          {allTasks.length > 0 ? (
            <div className="flex flex-col gap-3">
              {SEMAFORO_SECTIONS.map(section => {
                const sectionTasks = groupedTasks[section.key] || [];
                if (sectionTasks.length === 0) return null;
                const SectionIcon = section.icon;
                return (
                  <SemaforoSection
                    key={section.key}
                    section={section}
                    tasks={sectionTasks}
                    onStatusChange={onStatusChange}
                    onPersonalStatusChange={onPersonalStatusChange}
                    onEditPersonalTask={onEditPersonalTask}
                    navigate={navigate}
                  />
                );
              })}
            </div>
          ) : (
            <div className="p-6 text-center text-muted-foreground text-sm">
              Nessuna task per questo periodo
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function SemaforoSection({ section, tasks, onStatusChange, onPersonalStatusChange, onEditPersonalTask, navigate }) {
  const SectionIcon = section.icon;
  const sortedTasks = [...tasks].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid={`section-${section.key}`}>
      {/* Section Header */}
      <div className={`px-4 sm:px-5 py-2.5 ${section.bgLight} border-b ${section.borderColor} flex items-center gap-2.5`}>
        <SectionIcon className={`w-4 h-4 ${section.key === "scaduto" ? "text-zinc-600" : section.key === "rosso" ? "text-red-600" : section.key === "giallo" ? "text-amber-600" : section.key === "verde" ? "text-emerald-600" : "text-slate-500"}`} />
        <span className={`text-xs font-semibold uppercase tracking-wider ${section.key === "scaduto" ? "text-zinc-700" : section.key === "rosso" ? "text-red-700" : section.key === "giallo" ? "text-amber-700" : section.key === "verde" ? "text-emerald-700" : "text-slate-600"}`}>
          {section.label} ({tasks.length})
        </span>
      </div>
      {/* Task Rows */}
      <div className="divide-y divide-slate-100 px-1 sm:px-2">
        {sortedTasks.map((task, idx) => (
          <TaskRow
            key={task.task_id || `task-${idx}`}
            task={task}
            idx={idx}
            onStatusChange={onStatusChange}
            onPersonalStatusChange={onPersonalStatusChange}
            onEditPersonalTask={onEditPersonalTask}
            navigate={navigate}
          />
        ))}
      </div>
    </div>
  );
}

function TaskRow({ task, idx, onStatusChange, onPersonalStatusChange, onEditPersonalTask, navigate }) {
  const handleStatusChange = (taskId, newStatus) => {
    if (task.source === "personal") {
      onPersonalStatusChange(taskId, newStatus);
    } else {
      onStatusChange(taskId, newStatus);
    }
  };

  return (
    <div
      className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 hover:bg-muted/30 transition-colors text-sm"
      data-testid={`grouped-task-row-${idx}`}
    >
      {/* Source badge */}
      <Badge
        className={`text-xs shrink-0 w-12 justify-center ${
          task.source === "personal"
            ? "bg-violet-100 text-violet-700 border-violet-200"
            : "bg-sky-100 text-sky-700 border-sky-200"
        }`}
      >
        {task.source === "personal" ? "Pers." : "Auto"}
      </Badge>

      {/* Client name */}
      <span className="font-medium text-foreground w-36 truncate shrink-0" title={task.client_name}>
        {task.client_name}
      </span>

      {/* Task name */}
      <span className="text-muted-foreground flex-1 truncate">
        {task.source === "personal"
          ? task.title
          : etichettaTask(task, TASK_TYPE_LABELS)}
      </span>

      {/* Due date */}
      <span className="text-muted-foreground whitespace-nowrap shrink-0 w-20 text-right">
        <Calendar className="w-3 h-3 inline mr-1" />
        {task.due_date
          ? new Date(task.due_date).toLocaleDateString("it-IT", { day: "2-digit", month: "short" })
          : "-"}
      </span>

      {/* Actions */}
      <div className="flex gap-1 items-center shrink-0 w-44 justify-end" onClick={(e) => e.stopPropagation()}>
        {task.status !== "completato" && (
          <>
            {task.status === "da_fare" && (
              <Button
                size="sm"
                variant="outline"
                className="text-xs h-7 px-2"
                onClick={() => handleStatusChange(task.task_id, "in_corso")}
                data-testid={`start-task-${idx}`}
              >
                <Play className="w-3 h-3 mr-1" />
                Avvia
              </Button>
            )}
            {task.status === "in_corso" && (
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs mr-1">In corso</Badge>
            )}
            <Button
              size="sm"
              variant="outline"
              className="text-xs h-7 px-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border-emerald-200"
              onClick={() => handleStatusChange(task.task_id, "completato")}
              data-testid={`complete-task-${idx}`}
            >
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Fatto
            </Button>
          </>
        )}
        {task.source === "personal" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-slate-500 hover:text-indigo-600"
            onClick={() => onEditPersonalTask(task)}
            data-testid={`edit-personal-${idx}`}
          >
            <Pencil className="w-3.5 h-3.5" />
          </Button>
        )}
        {task.source === "system" && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600"
            onClick={() => navigate(`/task/${task.task_id}`)}
            data-testid={`view-task-${idx}`}
          >
            <ListTodo className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}
